// src/components/Education.tsx

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';
import { KeenIcon } from '@/components';
import { TimelinesWrapper } from '@/partials/timelines/default/item';
import { formatDateOnly } from '@/pages/buddy/utils/Date.tsx';

interface EducationItem {
    is_gap: boolean;
    gap_reason?: string | null;
    institution: string;
    degree: string;
    field_of_study: string;
    start_date: string | null;
    end_date: string | null;
    achievements: string[];
    country: string | null;
    city: string | null;
}

interface EducationProps {
    title: string;
    items: EducationItem[];
    candidate_p_id: string;
}

/** Combine city + country -> location */
function transformForEditing(items: EducationItem[]) {
    return items.map((item) => ({
        ...item,
        location: [item.city, item.country].filter(Boolean).join(', '),
    }));
}

/** Re-split location -> city, country */
function transformForSaving(editedItems: (EducationItem & { location?: string })[]) {
    return editedItems.map((item) => {
        const [cityStr, countryStr] = (item.location || '').split(',').map((s) => s.trim());
        return {
            ...item,
            city: cityStr || '',
            country: countryStr || '',
        };
    });
}

function deepCloneEducationItem(item: EducationItem & { location?: string }) {
    return {
        ...item,
        achievements: [...item.achievements],
    };
}

function deepCloneItems(items: (EducationItem & { location?: string })[]) {
    return items.map(deepCloneEducationItem);
}

function daysBetweenDates(date1: string, date2: string): number {
    const d1 = new Date(date1);
    const d2 = new Date(date2);
    const msPerDay = 1000 * 60 * 60 * 24;
    return Math.round((d2.getTime() - d1.getTime()) / msPerDay);
}

function buildGapDurationString(numDays: number): string {
    const approxYears = Math.floor(numDays / 365);
    const remainderAfterYears = numDays % 365;
    const approxMonths = Math.floor(remainderAfterYears / 30);
    const remainderAfterMonths = remainderAfterYears % 30;
    const days = remainderAfterMonths;

    const parts: string[] = [];
    if (approxYears > 0) parts.push(`${approxYears} year${approxYears > 1 ? 's' : ''}`);
    if (approxMonths > 0) parts.push(`${approxMonths} month${approxMonths > 1 ? 's' : ''}`);
    if (days > 0) parts.push(`${days} day${days > 1 ? 's' : ''}`);

    return parts.length ? parts.join(' ') : 'Less than a month';
}

interface GapRecord {
    __isGap: true;
    gapText: string;
}

/**
 * insertGapItems:
 *   We assume records are in descending order by start_date.
 *   Compare current record's end_date to next record's start_date.
 */
function insertGapItems(items: (EducationItem & { location?: string })[]): Array<(EducationItem & { location?: string }) | GapRecord> {
    if (items.length < 2) return items;

    const merged: Array<(EducationItem & { location?: string }) | GapRecord> = [];

    for (let i = 0; i < items.length; i++) {
        merged.push(items[i]);

        if (i < items.length - 1) {
            const currentItem = items[i];
            const nextItem = items[i + 1];

            if (currentItem.end_date && nextItem.start_date) {
                const gapInDays = daysBetweenDates(currentItem.end_date, nextItem.start_date);
                if (gapInDays > 30) {
                    merged.push({
                        __isGap: true,
                        gapText: `Gap in between records of ${buildGapDurationString(gapInDays)}`,
                    });
                }
            }
        }
    }
    return merged;
}

/**
 * Sort array in descending order by start_date.
 */
function sortItemsDescending(items: (EducationItem & { location?: string })[]): (EducationItem & { location?: string })[] {
    return [...items].sort((a, b) => {
        const dateA = a.start_date ? new Date(a.start_date).getTime() : 0;
        const dateB = b.start_date ? new Date(b.start_date).getTime() : 0;
        return dateB - dateA;
    });
}

const Education = ({ title, items, candidate_p_id }: EducationProps) => {
    const API_URL = import.meta.env.VITE_APP_API_URL;

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [originalItems, setOriginalItems] = useState<(EducationItem & { location?: string })[]>([]);
    const [editedItems, setEditedItems] = useState<(EducationItem & { location?: string })[]>([]);
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        const transformed = transformForEditing(items);
        setOriginalItems(deepCloneItems(transformed));
        setEditedItems(deepCloneItems(transformed));
    }, [items]);

    const calculateDuration = (startDate: string, endDate: string | null) => {
        const start = new Date(startDate);
        const today = endDate ? new Date(endDate) : new Date();
        const years = today.getFullYear() - start.getFullYear();
        const months = today.getMonth() - start.getMonth();
        const days = today.getDate() - start.getDate();
        let duration = '';
        if (years > 0) duration += `${years} year${years > 1 ? 's' : ''}`;
        if (months > 0) duration += duration ? ` ${months} month${months > 1 ? 's' : ''}` : `${months} month${months > 1 ? 's' : ''}`;
        if (days > 0) duration += duration ? ` ${days} day${days > 1 ? 's' : ''}` : `${days} day${days > 1 ? 's' : ''}`;
        return duration || 'Less than a month';
    };

    const handleEditClick = () => {
        setEditedItems(deepCloneItems(originalItems));
        setIsEditing(true);
    };
    const handleCancelClick = () => {
        setEditedItems(deepCloneItems(originalItems));
        setIsEditing(false);
    };

    const handleSaveClick = async () => {
        setLoading(true);

        // Validation checks for non-gap items and gap items
        for (const item of editedItems) {
            if (!item.is_gap) {
                if (!item.degree || !item.degree.trim()) {
                    enqueueSnackbar("Every education must have a degree.", { variant: 'warning' });
                    setLoading(false);
                    return;
                }
                if (item.start_date && item.end_date) {
                    if (new Date(item.start_date) > new Date(item.end_date)) {
                        enqueueSnackbar("The start date of an education cannot be after end date.", { variant: 'warning' });
                        setLoading(false);
                        return;
                    }
                }
            } else {
                // Validation for gap items
                if (!item.start_date || !item.start_date.trim()) {
                    enqueueSnackbar("Every gap must have a start date.", { variant: 'warning' });
                    setLoading(false);
                    return;
                }
                if (!item.end_date || !item.end_date.trim()) {
                    enqueueSnackbar("Every gap must have an end  date.", { variant: 'warning' });
                    setLoading(false);
                    return;
                }
                if (!item.gap_reason || !item.gap_reason.trim()) {
                    enqueueSnackbar("Every gap must have a reason.", { variant: 'warning' });
                    setLoading(false);
                    return;
                }
            }
        }

        try {
            const token = localStorage.getItem('authToken') || '';
            const dataToSend = transformForSaving(editedItems);
            await axios.put(
                `${API_URL}/candidate/${candidate_p_id}/education`,
                { education: dataToSend },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                }
            );
            const transformedNewOriginal = transformForEditing(dataToSend);
            setOriginalItems(deepCloneItems(transformedNewOriginal));
            setEditedItems(deepCloneItems(transformedNewOriginal));
            setIsEditing(false);
        } catch (err) {
            enqueueSnackbar(`Failed to update education: ${err}`, {
                variant: 'solid',
                state: 'danger',
            });
        } finally {
            setLoading(false);
        }
    };

    // Functions for adding items at top or bottom
    const createNewEducation = (): EducationItem & { location?: string } => ({
        is_gap: false,
        gap_reason: null,
        institution: '',
        degree: '',
        field_of_study: '',
        start_date: '',
        end_date: '',
        achievements: [],
        country: '',
        city: '',
        location: ''
    });

    const createNewGap = (): EducationItem & { location?: string } => ({
        is_gap: true,
        gap_reason: '',
        institution: '',
        degree: '',
        field_of_study: '',
        start_date: '',
        end_date: '',
        achievements: [],
        country: '',
        city: '',
        location: ''
    });

    const handleAddEducation = (atTop: boolean = false) => {
        setEditedItems((prev) => {
            const newItems = deepCloneItems(prev);
            if (atTop) {
                newItems.unshift(createNewEducation());
            } else {
                newItems.push(createNewEducation());
            }
            return newItems;
        });
    };

    const handleAddGap = (atTop: boolean = false) => {
        setEditedItems((prev) => {
            const newItems = deepCloneItems(prev);
            if (atTop) {
                newItems.unshift(createNewGap());
            } else {
                newItems.push(createNewGap());
            }
            return newItems;
        });
    };

    const handleDeleteEducation = (index: number) => {
        setEditedItems((prev) => {
            const newItems = deepCloneItems(prev);
            newItems.splice(index, 1);
            return newItems;
        });
    };

    type EditableStringFields =
        | 'institution'
        | 'degree'
        | 'field_of_study'
        | 'start_date'
        | 'end_date'
        | 'gap_reason'
        | 'country'
        | 'city'
        | 'location';

    const handleEditField = (index: number, field: EditableStringFields) =>
        (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
            setEditedItems((prev) => {
                const newItems = deepCloneItems(prev);
                newItems[index][field] = e.target.value;
                return newItems;
            });
        };

    const handleAddAchievement = (eduIndex: number) => {
        setEditedItems((prev) => {
            const newItems = deepCloneItems(prev);
            newItems[eduIndex].achievements.push('');
            return newItems;
        });
    };

    const handleEditAchievement =
        (eduIndex: number, achIndex: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
            setEditedItems((prev) => {
                const newItems = deepCloneItems(prev);
                newItems[eduIndex].achievements[achIndex] = e.target.value;
                return newItems;
            });
        };

    const handleDeleteAchievement = (eduIndex: number, achIndex: number) => {
        setEditedItems((prev) => {
            const newItems = deepCloneItems(prev);
            newItems[eduIndex].achievements.splice(achIndex, 1);
            return newItems;
        });
    };

    return (
        <div className="card">
            <div className="card-header flex justify-between items-center">
                <h3 className="card-title">{title}</h3>
                {!isEditing ? (
                    <button className="btn btn-light btn-sm" onClick={handleEditClick}>
                        <KeenIcon icon="notepad-edit" /> Edit
                    </button>
                ) : (
                    <div className="flex space-x-2">
                        <button className="btn btn-primary btn-sm" onClick={handleSaveClick} disabled={loading}>
                            <KeenIcon icon="file-up" /> {loading ? 'Saving...' : 'Save'}
                        </button>
                        <button className="btn btn-secondary btn-sm" onClick={handleCancelClick} disabled={loading}>
                            <KeenIcon icon="tablet-delete" /> Cancel
                        </button>
                    </div>
                )}
            </div>

            <div className="card-body">
                {!isEditing ? (
                    (() => {
                        const sortedDescending = sortItemsDescending(editedItems);
                        const merged = insertGapItems(sortedDescending);
                        return merged.map((item, i) => {
                            if ((item as GapRecord).__isGap) {
                                const gapRecord = item as GapRecord;
                                return (
                                    <TimelinesWrapper key={`gap-${i}`} icon="alarm" line>
                                        <div className="text-sm flex items-center justify-between">
                                            <span className="font-medium text-red-600">{gapRecord.gapText}</span>
                                        </div>
                                    </TimelinesWrapper>
                                );
                            }

                            const edu = item as EducationItem & { location?: string };

                            if (edu.is_gap) {
                                const startDate = edu.start_date ? formatDateOnly(edu.start_date) : '-';
                                let endDate = edu.end_date ? formatDateOnly(edu.end_date) : 'Until today';
                                let duration = '';
                                if (edu.start_date && edu.end_date) {
                                    duration = `(${calculateDuration(edu.start_date, edu.end_date)})`;
                                } else if (edu.start_date && !edu.end_date) {
                                    duration = `(${calculateDuration(edu.start_date, null)})`;
                                    endDate = 'Until today';
                                }
                                return (
                                    <TimelinesWrapper key={`gapdata-${i}`} icon="abstract-22" line>
                                        <div className="text-sm font-semibold text-red-500">Gap in timeline</div>
                                        <div className="flex items-center gap-2 mt-2 text-sm text-gray-700">
                                            <span>{startDate} - {endDate}</span>
                                            <span className="text-gray-500">{duration}</span>
                                        </div>
                                        {edu.gap_reason && (
                                            <div className="mt-1 text-sm text-gray-700">Reason: {edu.gap_reason}</div>
                                        )}
                                    </TimelinesWrapper>
                                );
                            }

                            const startDate = edu.start_date ? formatDateOnly(edu.start_date) : '-';
                            let endDate = edu.end_date ? formatDateOnly(edu.end_date) : 'Until today';
                            let duration = '';
                            if (edu.start_date && edu.end_date) {
                                duration = `(${calculateDuration(edu.start_date, edu.end_date)})`;
                            } else if (edu.start_date && !edu.end_date) {
                                duration = `(${calculateDuration(edu.start_date, null)})`;
                                endDate = 'Until today';
                            }

                            let locationDetails = '';
                            if (edu.city) locationDetails += edu.city;
                            if (edu.country) {
                                if (locationDetails) locationDetails += ', ';
                                locationDetails += edu.country;
                            }

                            return (
                                <TimelinesWrapper key={`edu-${i}`} icon="award" line>
                                    <div className="flex items-center justify-between">
                                        <h4 className="text-md font-semibold">
                                            {edu.degree} in {edu.field_of_study}
                                        </h4>
                                        <span className="text-sm text-gray-700">{edu.institution}</span>
                                    </div>
                                    <div className="flex items-center gap-2 mt-2">
                                        <span className="text-sm text-gray-700">{startDate} - {endDate}</span>
                                        <span className="text-sm text-gray-500">{duration}</span>
                                        <div className="text-sm text-gray-500 ml-auto flex items-center gap-2">
                                            {locationDetails && <span>{locationDetails}</span>}
                                        </div>
                                    </div>
                                    {edu.achievements.length > 0 && (
                                        <div className="card shadow-none p-4 mt-3">
                                            <div className="flex flex-wrap gap-2.5">
                                                <div className="flex flex-col gap-5 grow">
                                                    <div className="flex flex-col gap-2">
                                                        <ul className="text-sm text-gray-700">
                                                            {edu.achievements.map((ach, idx) => (
                                                                <div key={idx} className="flex items-center gap-2.5 w-full">
                                                                    <span>
                                                                        <KeenIcon icon="star" className="text-gray-500 text-sm" />
                                                                    </span>
                                                                    <span className="flex-1 text-sm">{ach}</span>
                                                                </div>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </TimelinesWrapper>
                            );
                        });
                    })()
                ) : (
                    <>
                        {/* Top Add Buttons */}
                        <div className="flex space-x-2 mb-4">
                            <button type="button" className="btn btn-success btn-sm" onClick={() => handleAddEducation(true)}>
                                <KeenIcon icon="add-notepad" /> Add Education
                            </button>
                            <button type="button" className="btn btn-success btn-sm" onClick={() => handleAddGap(true)}>
                                <KeenIcon icon="add-notepad" /> Add Gap
                            </button>
                        </div>

                        {/* Edit Mode Entries */}
                        {editedItems.map((item, index) => (
                            <TimelinesWrapper
                                key={index}
                                icon={item.is_gap ? "abstract-22" : "award"}
                                line
                            >
                                {item.is_gap ? (
                                    <>
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mt-2">
                                            <input
                                                type="date"
                                                className="input input-bordered input-sm w-full sm:w-auto"
                                                value={item.start_date ? item.start_date.slice(0, 10) : ''}
                                                onChange={handleEditField(index, 'start_date')}
                                            />
                                            <span className="text-sm">-</span>
                                            <input
                                                type="date"
                                                className="input input-bordered input-sm w-full sm:w-auto"
                                                value={item.end_date ? item.end_date.slice(0, 10) : ''}
                                                onChange={handleEditField(index, 'end_date')}
                                            />
                                        </div>
                                        <input
                                            type="text"
                                            className="input input-bordered input-sm w-full mt-2"
                                            placeholder="Gap Reason"
                                            value={item.gap_reason || ''}
                                            onChange={handleEditField(index, 'gap_reason')}
                                        />
                                    </>
                                ) : (
                                    <>
                                        <input
                                            type="text"
                                            className="input input-bordered input-sm w-full mt-1"
                                            placeholder="Institution"
                                            value={item.institution || ''}
                                            onChange={handleEditField(index, 'institution')}
                                        />
                                        <input
                                            type="text"
                                            className="input input-bordered input-sm w-full mt-2"
                                            placeholder="Degree"
                                            value={item.degree || ''}
                                            onChange={handleEditField(index, 'degree')}
                                        />
                                        <input
                                            type="text"
                                            className="input input-bordered input-sm w-full mt-2"
                                            placeholder="Field of Study"
                                            value={item.field_of_study || ''}
                                            onChange={handleEditField(index, 'field_of_study')}
                                        />
                                        <input
                                            type="text"
                                            className="input input-bordered input-sm w-full mt-2"
                                            placeholder="Location (City, Country)"
                                            value={item.location || ''}
                                            onChange={handleEditField(index, 'location')}
                                        />
                                        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 mt-2">
                                            <input
                                                type="date"
                                                className="input input-bordered input-sm w-full sm:w-auto"
                                                value={item.start_date ? item.start_date.slice(0, 10) : ''}
                                                onChange={handleEditField(index, 'start_date')}
                                            />
                                            <span className="text-sm">-</span>
                                            <input
                                                type="date"
                                                className="input input-bordered input-sm w-full sm:w-auto"
                                                value={item.end_date ? item.end_date.slice(0, 10) : ''}
                                                onChange={handleEditField(index, 'end_date')}
                                            />
                                        </div>
                                        <div className="card shadow-none p-4 mt-3">
                                            {item.achievements.map((ach, achIndex) => (
                                                <div key={achIndex} className="flex items-center gap-2.5 w-full mb-2">
                                                    <input
                                                        type="text"
                                                        className="input input-bordered input-sm flex-1"
                                                        placeholder="Achievement"
                                                        value={ach}
                                                        onChange={handleEditAchievement(index, achIndex)}
                                                    />
                                                    <button
                                                        type="button"
                                                        className="btn btn-ghost btn-sm text-red-500"
                                                        onClick={() => handleDeleteAchievement(index, achIndex)}
                                                        aria-label={`Delete achievement ${ach}`}
                                                    >
                                                        &times;
                                                    </button>
                                                </div>
                                            ))}
                                            <div className="flex justify-between">
                                                <button
                                                    type="button"
                                                    className="btn btn-success flex btn-sm"
                                                    onClick={() => handleAddAchievement(index)}
                                                >
                                                    <KeenIcon icon="add-notepad" /> Add Achievement
                                                </button>
                                            </div>
                                        </div>
                                    </>
                                )}
                                <div className="mt-2 flex justify-end">
                                    <button
                                        type="button"
                                        className="btn btn-ghost btn-sm text-red-500"
                                        onClick={() => handleDeleteEducation(index)}
                                        aria-label="Delete education"
                                    >
                                        <KeenIcon icon="trash" /> Delete this {item.is_gap ? 'Gap' : 'Education'}
                                    </button>
                                </div>
                            </TimelinesWrapper>
                        ))}

                        {/* Bottom Add Buttons */}
                        <div className="flex space-x-2 mt-4">
                            <button type="button" className="btn btn-success btn-sm" onClick={() => handleAddEducation()}>
                                <KeenIcon icon="add-notepad" /> Add Education
                            </button>
                            <button type="button" className="btn btn-success btn-sm" onClick={() => handleAddGap()}>
                                <KeenIcon icon="add-notepad" /> Add Gap
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export { Education };
